import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getApiErrorMessage } from '../services/apiService';
import {
  PROJEKTTERVEZES_FELADAT_COLOR_PRESETS,
  ProjekttervezesCsoport,
} from '../services/projekttervezesService';

export interface CsoportDialogValue {
  nev: string;
  leiras: string;
  szin: string;
}

interface AddProjekttervezesCsoportDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialCsoport?: ProjekttervezesCsoport | null;
  onClose: () => void;
  onSave: (value: CsoportDialogValue) => Promise<void>;
}

const emptyValue = (): CsoportDialogValue => ({
  nev: '',
  leiras: '',
  szin: PROJEKTTERVEZES_FELADAT_COLOR_PRESETS[0],
});

const AddProjekttervezesCsoportDialog: React.FC<AddProjekttervezesCsoportDialogProps> = ({
  open,
  mode,
  initialCsoport,
  onClose,
  onSave,
}) => {
  const [value, setValue] = useState<CsoportDialogValue>(emptyValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }

    if (mode === 'edit' && initialCsoport) {
      setValue({
        nev: initialCsoport.nev,
        leiras: initialCsoport.leiras,
        szin: initialCsoport.szin,
      });
    } else {
      setValue(emptyValue());
    }
    setError('');
  }, [open, mode, initialCsoport]);

  const handleSave = async () => {
    if (!value.nev.trim()) {
      setError('A csoport neve kötelező.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await onSave(value);
      onClose();
    } catch (saveError: any) {
      setError(getApiErrorMessage(saveError, 'Nem sikerült menteni a csoportot.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'create' ? 'Csoport hozzáadása' : 'Csoport szerkesztése'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label="Név"
            value={value.nev}
            onChange={(event) => setValue((prev) => ({ ...prev, nev: event.target.value }))}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Leírás"
            value={value.leiras}
            onChange={(event) => setValue((prev) => ({ ...prev, leiras: event.target.value }))}
            fullWidth
            multiline
            minRows={2}
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Szín
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {PROJEKTTERVEZES_FELADAT_COLOR_PRESETS.map((color) => (
                <Box
                  key={color}
                  component="button"
                  type="button"
                  onClick={() => setValue((prev) => ({ ...prev, szin: color }))}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    border: value.szin === color ? '2px solid #1976d2' : '1px solid #d0d7de',
                    backgroundColor: color,
                    cursor: 'pointer',
                    p: 0,
                  }}
                  aria-label={color}
                />
              ))}
            </Stack>
            <TextField
              size="small"
              label="Hex szín"
              value={value.szin}
              onChange={(event) => setValue((prev) => ({ ...prev, szin: event.target.value }))}
              sx={{ mt: 1.5, width: 140 }}
            />
          </Box>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Mégse</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {mode === 'create' ? 'Hozzáadás' : 'Mentés'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProjekttervezesCsoportDialog;

export const csoportDialogValueToPayload = (value: CsoportDialogValue) => ({
  nev: value.nev.trim(),
  leiras: value.leiras.trim(),
  szin: value.szin,
});
